import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow
} from "@material-ui/core";
import { isStaffRole } from "@saleor/auth/hooks/useUserRole";
import { DateTime } from "@saleor/components/Date";
import MyTypography from "@saleor/components/MyTypography";
import Skeleton from "@saleor/components/Skeleton";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { BIG_ROW_NUMBER_SET } from "@saleor/components/TablePagination/constants";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import { ContractListUrlSortField } from "@saleor/createOrder/urls";
import {
  ContractsQuery,
  useDropdownListMaterialGroup1Query
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps, RelayToFlat, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import NotFoundContract from "../NotFoundContract";

const useStyles = makeStyles(
  theme => ({
    root: {
      overflowX: "auto",
      width: "100%",
      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      },
      "& .MuiTableCell-head": {
        "& *": {
          color: "#696969"
        }
      }
    },
    cursorPointer: {
      cursor: "pointer"
    },
    colBU: {
      color: theme.palette.grey[900]
    },
    colCompany: {
      whiteSpace: "nowrap",
      color: theme.palette.grey[900]
    },
    colContractNo: {
      "& .MuiTypography-root": {
        color: theme.palette.info.main
      }
    },
    colProjectName: {
      whiteSpace: "nowrap",
      color: theme.palette.grey[800]
    },
    colMaterialGroup: {
      whiteSpace: "nowrap",
      color: theme.palette.grey[800]
    },
    colDate: {
      color: theme.palette.grey[800]
    },
    colPayment: {
      color: theme.palette.grey[800],
      whiteSpace: "nowrap"
    },
    link: {
      cursor: "pointer"
    },
    noFilteredContract: {
      fontFamily: "inherit",
      fontStyle: "italic",
      letterSpacing: 0,
      color: theme.palette.info.main
    },
    wrapper: {
      background: "rgba(212, 244, 248, 0.8)"
    }
  }),
  { name: "SoldToList" }
);

const numberOfColumns = 8;

interface SoldToListProps
  extends ListProps,
    SortPage<ContractListUrlSortField> {
  customerContracts: RelayToFlat<ContractsQuery["contracts"]>;
  soldToExists?: boolean;
  noFilterInput?: boolean;
  totalCount: number | null;
  onViewContract: (
    contractId: string,
    contractNo: string,
    salesOrgCode: string,
    soldToCode: string
  ) => void;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  latestPageItemNumber: number | null;
}

const SoldToList: React.FC<SoldToListProps> = props => {
  const {
    disabled,
    settings,
    customerContracts,
    soldToExists,
    noFilterInput,
    pageInfo,
    sort,
    totalCount,
    onPreviousPage,
    onNextPage,
    onUpdateListSettings,
    onViewContract,
    onSort,
    onPreviousFirstPage,
    onNextLastPage,
    latestPageItemNumber
  } = props;
  const classes = useStyles(props);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [customerContractsBuffer, setCustomerContractsBuffer] = useState(
    customerContracts
  );
  const [soldToExistsBuffer, setSoldToExistsBuffer] = useState(soldToExists);

  const { data: productGroup } = useDropdownListMaterialGroup1Query({
    variables: { bu: "PP" }
  });
  useEffect(() => {
    if (!disabled) {
      setCustomerContractsBuffer(customerContracts);
      setSoldToExistsBuffer(soldToExists);
    }
  }, [disabled, customerContracts, soldToExists]);

  const companyName = (code: string) => {
    switch (code) {
      case "0750":
        return "SIAM KRAFT";
      case "7540":
        return "THAI CANE";

      default:
        return "Not found";
    }
  };

  const prcGroupLabels = useMemo(
    () =>
      (productGroup?.dropdownListMaterialGroup1 ?? []).reduce(
        (mapGroupLabels, group) => {
          mapGroupLabels[
            group.materialGroup1
          ] = `${group.materialGroup1}: ${group.materialGroup1Desc}`;
          return mapGroupLabels;
        },
        {}
      ),
    [productGroup]
  );

  return (
    soldToExistsBuffer && (
      <>
        <div className={classes.root} ref={containerRef}>
          {customerContractsBuffer?.length || noFilterInput ? (
            <Table>
              <TableHead>
                <TableRow>
                  {isStaffRole() && (
                    <TableCellHeader className={classes.colBU}>
                      <FormattedMessage defaultMessage="BU" />
                    </TableCellHeader>
                  )}
                  <TableCellHeader className={classes.colCompany}>
                    <FormattedMessage defaultMessage="Company" />
                  </TableCellHeader>
                  <TableCellHeader
                    className={classes.colContractNo}
                    direction={
                      sort.sort === ContractListUrlSortField.contractNo
                        ? getArrowDirection(sort.asc)
                        : undefined
                    }
                    arrowPosition="right"
                    onClick={() => onSort(ContractListUrlSortField.contractNo)}
                  >
                    <FormattedMessage defaultMessage="เลขที่ Contract" />
                  </TableCellHeader>
                  <TableCellHeader className={classes.colProjectName}>
                    <FormattedMessage defaultMessage="ชื่อโครงการ" />
                  </TableCellHeader>

                  <TableCellHeader className={classes.colMaterialGroup}>
                    <FormattedMessage defaultMessage="ชื่อกลุ่มสินค้า " />
                  </TableCellHeader>

                  <TableCellHeader className={classes.colDate}>
                    <FormattedMessage defaultMessage="วันที่เริ่มต้น" />
                  </TableCellHeader>
                  <TableCellHeader className={classes.colDate}>
                    <FormattedMessage defaultMessage="วันที่สิ้นสุด" />
                  </TableCellHeader>
                  <TableCellHeader
                    textAlign="right"
                    className={classes.colPayment}
                  >
                    <FormattedMessage defaultMessage="เงื่อนไขการชำระเงิน" />
                  </TableCellHeader>
                </TableRow>
              </TableHead>
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={numberOfColumns}
                    className={classes.wrapper}
                  >
                    <TablePaginationCustom
                      settings={settings}
                      hasNextPage={
                        pageInfo && !disabled ? pageInfo.hasNextPage : false
                      }
                      onPreviousFirstPage={onPreviousFirstPage}
                      onNextLastPage={onNextLastPage}
                      rowNumberSet={BIG_ROW_NUMBER_SET}
                      onNextPage={onNextPage}
                      onUpdateListSettings={onUpdateListSettings}
                      hasPreviousPage={
                        pageInfo && !disabled ? pageInfo.hasPreviousPage : false
                      }
                      isBackFistPage
                      isNextLastPage
                      onPreviousPage={onPreviousPage}
                      totalCount={totalCount}
                      isShowIndex
                      latestPageItemNumber={latestPageItemNumber}
                      disabled={disabled}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
              <TableBody>
                {noFilterInput ? (
                  <TableRow>
                    <TableCell
                      colSpan={numberOfColumns}
                      className={classes.noFilteredContract}
                    >
                      <FormattedMessage defaultMessage="กรุณาเลือกตัวกรองรายการ เพื่อแสดงข้อมูล" />
                    </TableCell>
                  </TableRow>
                ) : (
                  renderCollection(
                    customerContractsBuffer,
                    (contract, index) => (
                      <TableRow
                        key={index}
                        data-testid="order-table-row"
                        hover={!!contract}
                        className={classes.cursorPointer}
                        onClick={() =>
                          onViewContract(
                            contract.id,
                            contract?.contractNo,
                            contract?.salesOrganization?.code,
                            contract?.customer?.soldToCode
                          )
                        }
                      >
                        {isStaffRole() && (
                          <TableCell className={classes.colBU}>
                            {contract?.company ? (
                              contract.company?.businessUnit?.name
                            ) : (
                              <Skeleton />
                            )}
                          </TableCell>
                        )}
                        <TableCell className={classes.colCompany}>
                          {contract ? (
                            companyName(contract?.salesOrganization?.code)
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                        <TableCell className={classes.colContractNo}>
                          <MyTypography variant="subtitle3">
                            {contract ? contract.contractNo : <Skeleton />}
                          </MyTypography>
                        </TableCell>
                        <TableCell className={classes.colProjectName}>
                          {contract ? contract.projectName : <Skeleton />}
                        </TableCell>

                        <TableCell className={classes.colProjectName}>
                          {contract ? (
                            prcGroupLabels?.[contract.prcGroup1]
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>

                        <TableCell className={classes.colDate}>
                          {maybe(() => contract.startDate) ? (
                            <DateTime
                              date={contract.startDate}
                              format="DD/MM/YYYY"
                            />
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                        <TableCell className={classes.colDate}>
                          {maybe(() => contract.endDate) ? (
                            <DateTime
                              date={contract.endDate}
                              format="DD/MM/YYYY"
                            />
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                        <TableCell className={classes.colPayment} align="right">
                          {maybe(() => contract.paymentTermKey) ? (
                            `${contract.paymentTermKey} - ${contract?.paymentTermDescTh}`
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  )
                )}
              </TableBody>
            </Table>
          ) : disabled ? (
            ""
          ) : (
            <NotFoundContract />
          )}
          {customerContractsBuffer?.length ? (
            <StickyHorizontalScrollBar container={containerRef} />
          ) : null}
        </div>
      </>
    )
  );
};
SoldToList.displayName = "SoldToList";
export default SoldToList;
